import type { UseFetchOptions } from 'nuxt/app'
import type { StrapiApiDefaultResponse } from '~/composables/types/strapi-api'
import type { StrapiMagazineArticle } from '~/composables/types/strapi/strapi-magazine'
import { dynamicContentTransformer } from '~/utils/dynamicContentTransformer'

const getStrapiArticlesUrl = ({
  slug,
  query,
  locale,
  strapiApiBaseURL,
}: {
  slug?: string
  query?: Record<string, string>
  locale?: string
  strapiApiBaseURL: string
}) => {
  const articlesUrl = new URL(`articles?sort=createdAt:desc`, strapiApiBaseURL)

  if (slug) {
    articlesUrl.searchParams.set('filters[slug][$eq]', slug)
  }

  if (query) {
    Object.keys(query).forEach((key) => {
      articlesUrl.searchParams.set(key, query[key])
    })
  }

  articlesUrl.searchParams.set('locale', locale || 'de-de')

  return articlesUrl
}

export function useStrapiArticles({
  slug,
  query,
  options = {},
  locale,
}: {
  slug?: string
  query?: Record<string, string>
  options?: UseFetchOptions<
    StrapiApiDefaultResponse<StrapiMagazineArticle[]>,
    StrapiMagazineArticle[]
  >
  locale?: string
} = {}) {
  const config = useRuntimeConfig()
  const { strapiApiBaseURL, strapiApiKey } = config.public
  const localeDefault = useI18n().locale
  const articlesUrl = getStrapiArticlesUrl({
    slug,
    query,
    locale: locale || localeDefault.value,
    strapiApiBaseURL,
  })
  const staticMetrics = useStaticMetrics()

  const defaults: UseFetchOptions<
    StrapiApiDefaultResponse<StrapiMagazineArticle[]>,
    StrapiMagazineArticle[]
  > = {
    baseURL: strapiApiBaseURL,
    key: articlesUrl.toString(),

    headers: {
      Authorization: `Bearer ${strapiApiKey}`,
    },
  }

  const baseTransform = (
    data: StrapiApiDefaultResponse<StrapiMagazineArticle[]>
  ): StrapiMagazineArticle[] => {
    return data.data.map((item) =>
      dynamicContentTransformer(item, staticMetrics)
    )
  }

  const params: UseFetchOptions<
    StrapiApiDefaultResponse<StrapiMagazineArticle[]>,
    StrapiMagazineArticle[]
  > = { ...defaults, ...options, transform: baseTransform }

  return useFetch(articlesUrl.toString(), params)
}
